import React, { Fragment, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get } from 'lodash-es';
import Button from 'reactstrap/lib/Button';
import { useToggle } from 'site-modules/shared/hooks/use-toggle';

import { CmsEntities } from 'client/data/models/cms';
import { HERO_SIZE } from 'site-modules/shared/constants/editorial/editorial';
import { getFullImageUrl, getStaticImageUrl, getValidImageUrl, SOURCES } from 'client/utils/image-helpers';
import { HtmlVideoPlayer } from 'site-modules/shared/components/html-video-player/html-video-player';

import './hero-content.scss';

const TOP_RATED_2026_LOGO_SRC = 'img/top-rated/2026/top-rated-2026-box-logo.svg';

export function HeroContent({
  content,
  className = null,
  showOverlayContent = true,
  isMobile = false,
  contentTitle = '',
}) {
  const [videoPlayback, toggleVideoPlayback] = useToggle(true);
  const ref = useRef();

  const videoUrlLg = content.metadata('videoLgURL').value() || content.metadata('videoURL').value();
  const videoUrlSm = content.metadata('videoSmURL').value() || content.metadata('videoURL').value();
  const videoUrl = isMobile ? videoUrlSm : videoUrlLg;
  const photoUrlLg = content.metadata('heroLgURL').value() || get(content.links(), '[0].href');
  const photoUrlSm = content.metadata('heroSmURL').value() || get(content.links(), '[0].href');
  const photoUrl = isMobile ? photoUrlSm : photoUrlLg;

  useEffect(() => {
    if (ref.current) {
      ref.current.classList.add('loaded');
    }
  }, []);

  if (!(videoUrl || photoUrl)) {
    return null;
  }
  const mediaHeight = isMobile ? 420 : 580;

  return (
    <figure className={classnames('hero-content-rect pos-r', className)} ref={ref}>
      <div className="hero-bg">
        <div className="hero-bg pos-r">
          {videoUrl ? (
            <Fragment>
              <HtmlVideoPlayer
                className="hero-video w-100 d-block pos-r"
                height={mediaHeight}
                src={getValidImageUrl(videoUrl, SOURCES.STATIC)}
                autoPlay
                loop
                playsInline
                playback={videoPlayback}
                poster={getValidImageUrl(videoUrl, SOURCES.STATIC).replace('mp4', 'jpg')}
                controls={false}
              />
              <div className="video-controls d-flex pos-a">
                <Button
                  className="play-pause-btn d-flex align-items-center justify-content-center p-1"
                  onClick={toggleVideoPlayback}
                  title={videoPlayback ? 'Pause' : 'Play'}
                >
                  <span
                    className={classnames('size-20 text-white', {
                      'icon-pause21': videoPlayback,
                      'icon-play41': !videoPlayback,
                    })}
                    aria-hidden
                  />
                </Button>
              </div>
            </Fragment>
          ) : (
            <img
              className="hero-image w-100 image-only"
              src={getFullImageUrl(photoUrl, HERO_SIZE[isMobile])}
              alt=""
              height={mediaHeight}
              fetchPriority="high"
            />
          )}

          <h1 className="hero-text pos-a center-x top-0 pt-2 mt-2_5 mt-md-0 d-flex flex-column w-100">
            {showOverlayContent && (
              <Fragment>
                <img
                  src={getStaticImageUrl(TOP_RATED_2026_LOGO_SRC)}
                  alt=""
                  height={isMobile ? '98' : '128'}
                  className="mx-auto mt-md-1"
                  style={{ maxWidth: '68%' }}
                />
              </Fragment>
            )}
            <span
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textIndent: '100%',
                display: 'inline-block',
              }}
            >
              {' '}
              {contentTitle}{' '}
            </span>
          </h1>
        </div>
      </div>
    </figure>
  );
}

HeroContent.propTypes = {
  content: CmsEntities.Content.isRequired,
  className: PropTypes.string,
  showOverlayContent: PropTypes.bool,
  isMobile: PropTypes.bool,
  contentTitle: PropTypes.string,
};
